import React from "react";
import { Scale, AlertCircle, FileText } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function Terms() {
  return (
    <>
      <Helmet>
        <title>Terms of Service | Lo-Fee.com</title>
        <meta
          name="description"
          content="Read Lo-Fee.com's terms of service for our Canadian Bitcoin exchange fee comparison platform. Understand your rights and responsibilities when using our service."
        />
        <link rel="canonical" href="https://lo-fee.com/terms" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-8">
          <header className="space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">
              Terms of Service
            </h1>
            <p className="text-lg text-[--text-secondary]">
              By using Lo-Fee.com, you agree to these terms. Please read them
              carefully.
            </p>
          </header>

          <div className="bg-amber-50 border border-amber-200 p-6 rounded-lg">
            <div className="flex gap-4">
              <AlertCircle className="h-6 w-6 text-amber-500 flex-shrink-0" />
              <div className="space-y-2">
                <h2 className="text-lg font-bold text-amber-900">
                  Important Notice
                </h2>
                <p className="text-amber-800">
                  Lo-Fee.com is an information service only. We do not provide
                  financial advice, execute trades, or handle any cryptocurrency
                  transactions. Always verify rates and fees directly with
                  exchanges before trading.
                </p>
              </div>
            </div>
          </div>

          <section className="space-y-6">
            <h2 className="text-2xl font-bold">Service Description</h2>
            <div className="prose prose-lg max-w-none text-[--text-secondary]">
              <p>Lo-Fee.com provides:</p>
              <ul>
                <li>Real-time cryptocurrency exchange fee comparisons</li>
                <li>Price tracking for Bitcoin in Canadian dollars</li>
                <li>Educational information about cryptocurrency trading</li>
                <li>Links to registered cryptocurrency exchanges</li>
              </ul>
            </div>
          </section>

          <section className="grid sm:grid-cols-3 gap-6">
            {[
              {
                icon: <Scale className="h-6 w-6 text-blue-500" />,
                title: "No Financial Advice",
                description:
                  "Our service is for informational purposes only. We do not provide investment advice.",
              },
              {
                icon: <FileText className="h-6 w-6 text-green-500" />,
                title: "Accuracy of Information",
                description:
                  "While we strive for accuracy, all data should be verified with exchanges.",
              },
              {
                icon: <AlertCircle className="h-6 w-6 text-red-500" />,
                title: "Risk Warning",
                description:
                  "Cryptocurrency trading involves substantial risk of loss.",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="p-6 bg-[--bg-secondary] border-2 border-[--border-color]"
              >
                {item.icon}
                <h3 className="text-lg font-bold mt-4 mb-2">{item.title}</h3>
                <p className="text-[--text-secondary]">{item.description}</p>
              </div>
            ))}
          </section>

          <section className="space-y-6">
            <h2 className="text-2xl font-bold">User Obligations</h2>
            <div className="prose prose-lg max-w-none text-[--text-secondary]">
              <p>By using Lo-Fee.com, you agree to:</p>
              <ul>
                <li>Use the service for lawful purposes only</li>
                <li>Not attempt to manipulate or abuse the service</li>
                <li>Not use automated systems to scrape or collect data</li>
                <li>Verify all information independently</li>
              </ul>
            </div>
          </section>

          <section className="space-y-6">
            <h2 className="text-2xl font-bold">Disclaimers</h2>
            <div className="prose prose-lg max-w-none text-[--text-secondary]">
              <p>Lo-Fee.com:</p>
              <ul>
                <li>Is provided "as is" without warranties of any kind</li>
                <li>May contain inaccuracies or errors</li>
                <li>May be unavailable or modified at any time</li>
                <li>Is not responsible for any trading decisions</li>
              </ul>
            </div>
          </section>

          <section className="space-y-6">
            <h2 className="text-2xl font-bold">Limitation of Liability</h2>
            <div className="prose prose-lg max-w-none text-[--text-secondary]">
              <p>
                Lo-Fee.com and its operators are not liable for any damages
                arising from the use of the service, including but not limited
                to:
              </p>
              <ul>
                <li>Trading losses</li>
                <li>Data inaccuracies</li>
                <li>Service interruptions</li>
                <li>Technical issues</li>
              </ul>
            </div>
          </section>

          <section className="bg-[--bg-secondary] border-2 border-[--border-color] p-6">
            <h2 className="text-xl font-bold mb-4">Changes to Terms</h2>
            <p className="text-[--text-secondary]">
              We reserve the right to modify these terms at any time. Continued
              use of the service constitutes acceptance of any changes.
            </p>
            <p className="text-sm text-[--text-secondary] mt-4">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </section>
        </div>
      </div>
    </>
  );
}
